import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Wallet, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { dispatchCashFlowInvalidate } from '../../common/cashFlowEvents';
import { httpClient } from '../../common/httpclient';
import { formatCurrency } from '../../common/formatters';
import { toIsoYyyyMmDd, transactionListDateToIso } from '../../common/transactionDates';
import { EXPENSE_STATUS, updateExpenseStatus } from '../../services/expenseStatusService';
import { settingRowSx, TxCompactTypeBadge, useStandaloneFieldSx } from './shared';

dayjs.extend(customParseFormat);

// `TransactionEditForm` — variante de edição compacta do formulário de
// transação (DESIGN-SYSTEM.md §5.6, item irmão de `Form.js`), reaproveita o
// mesmo drawer/protótipo (`prototipo-lista-transacoes.html`), não é uma tela
// separada. Único caminho de "Editar" hoje alcançável por `List.js`: despesa
// Pendente ou Vencida (`PUT /transactions/{id}` rejeita qualquer outro caso
// com `ErrTransactionNotEditable`, ver nota em `Form.js`).
//
// Badge "Despesa" somente-leitura no lugar dos radio-cards de tipo, campo
// Data oculto (a data de lançamento original não é reeditável nesta via
// rápida — nem sequer é mostrada aqui, mas continua sendo reenviada com o
// valor original no PUT, já que a API exige o campo). Editáveis: Descrição,
// Valor, Vencimento e o toggle "Pagar pelo caixa agora".
//
// "Pagar pelo caixa agora": não existe (e não foi criado) nenhum endpoint
// novo para isso — compõe dois endpoints já existentes e já usados em
// `List.js`: primeiro `PUT /transactions/{id}` (edita os campos), depois,
// só se o toggle estiver ativo, `PUT /transactions/expense` via
// `updateExpenseStatus` (mesma função usada pela ação "Prestar contas" da
// lista) com `status: Prestado Contas` e `payment_date` de hoje — mesma
// semântica de "pagar agora" já usada em `Form.js`/na criação de despesa.
//
// Vencimento fica opcional (não obrigatório como no protótipo estático):
// `GET /transactions/query` não devolve `due_date` (lacuna de backend já
// documentada em `List.js`/`Form.js`), então o campo sempre abre em branco
// aqui — torná-lo obrigatório forçaria reescrever o vencimento a cada
// edição. Deixar em branco preserva o vencimento já cadastrado
// (`UpdateLancamentoDespesaPendenteOuVencido` faz `COALESCE($due, due_date)`
// no backend).
//
// Sem campo de Observações (mesma decisão de `Form.js`): o protótipo não
// modela esse campo na variante compacta, e `GET /transactions/query`
// também não devolve `observations`, então o valor inicial já era sempre
// vazio — removê-lo da UI não perde nenhum dado que já fosse visível aqui.

function amountFromRowToInput(amount) {
    if (amount == null || amount === '') return '';
    if (typeof amount === 'number' && Number.isFinite(amount)) {
        return formatCurrency(String(Math.round(amount * 100 + Number.EPSILON)));
    }
    return formatCurrency(String(amount));
}

function readApiErrorMessage(response) {
    const data = response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (data && typeof data === 'object' && data.error) return String(data.error);
    return '';
}

const optionalIsoDateSchema = Yup.string().test(
    'due-iso',
    'Data de vencimento inválida',
    (val) => {
        if (!val || !String(val).trim()) return true;
        const s = String(val).trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
        return dayjs(s, 'YYYY-MM-DD', true).isValid();
    }
);

export default function TransactionEditForm({ transaction, onClose, onSuccess, onListRefresh }) {
    const standaloneFieldSx = useStandaloneFieldSx();
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const initialValues = useMemo(() => {
        const rawDue =
            transaction?.due_date ??
            transaction?.dueDate ??
            transaction?.data_vencimento;
        const dueIso = transactionListDateToIso(rawDue);

        return {
            description: transaction?.description ?? '',
            amount: amountFromRowToInput(transaction?.amount),
            dueDate: dueIso || '',
            payNow: false
        };
    }, [transaction]);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                description: Yup.string()
                    .trim()
                    .required('Descrição é obrigatória')
                    .min(3, 'Descrição deve ter pelo menos 3 caracteres'),
                dueDate: optionalIsoDateSchema,
                amount: Yup.string()
                    .required('Valor é obrigatório')
                    .test('is-positive', 'Valor deve ser maior que zero', function (value) {
                        if (!value) return false;
                        const numericValue = parseFloat(
                            String(value).replace(/[^\d,.-]/g, '').replace(',', '.')
                        );
                        return !Number.isNaN(numericValue) && numericValue > 0;
                    }),
                payNow: Yup.boolean()
            }),
        []
    );

    // Data de lançamento original — oculta nesta via rápida, mas reenviada
    // sem alteração no PUT (a API exige `date`).
    const originalDateIso = useMemo(
        () => transactionListDateToIso(transaction?.date_created) || dayjs().format('YYYY-MM-DD'),
        [transaction]
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema,
        onSubmit: async (values) => {
            setSubmitError('');
            setLoading(true);
            try {
                const payload = {
                    description: values.description.trim(),
                    date: originalDateIso,
                    amount: values.amount,
                    observations: ''
                };
                const due = values.dueDate && String(values.dueDate).trim();
                if (due) {
                    payload.due_date = due;
                }

                const res = await httpClient.put(`/transactions/${transaction.id}`, payload);

                if (res.status !== 204) {
                    if (res.status === 409 || res.status === 404) {
                        await onListRefresh?.();
                    }
                    let msg = readApiErrorMessage(res);
                    if (res.status === 400 && !msg) {
                        msg = 'Dados inválidos. Verifique descrição, vencimento e valor.';
                    } else if (res.status === 404 && !msg) {
                        msg = 'Lançamento não encontrado para esta empresa.';
                    } else if (res.status === 409 && !msg) {
                        msg =
                            'Este lançamento não pode ser alterado. Só despesas pendentes ou vencidas podem ser editadas.';
                    } else if (res.status === 500 && !msg) {
                        msg = 'Erro interno no servidor. Tente novamente ou contate o suporte.';
                    } else if (!msg) {
                        msg = 'Não foi possível salvar as alterações.';
                    }
                    setSubmitError(msg);
                    return;
                }

                // Edição salva. Se "Pagar pelo caixa agora" estiver ativo,
                // confirma o pagamento já em seguida (compõe o mesmo
                // endpoint/ação de "Prestar contas" da lista).
                if (values.payNow) {
                    try {
                        await updateExpenseStatus({
                            id: transaction.id,
                            status: EXPENSE_STATUS.PRESTADO_CONTAS,
                            payment_date: toIsoYyyyMmDd(dayjs())
                        });
                        dispatchCashFlowInvalidate();
                        onSuccess?.({ paidNow: true });
                        return;
                    } catch (e) {
                        await onListRefresh?.();
                        setSubmitError(
                            `A despesa foi atualizada, mas não foi possível confirmar o pagamento agora (${e.message || 'erro desconhecido'}). Use "Prestar contas" na lista para tentar novamente.`
                        );
                        return;
                    }
                }

                onSuccess?.({ paidNow: false });
            } catch (e) {
                setSubmitError(e.message || 'Erro de rede ao salvar.');
            } finally {
                setLoading(false);
            }
        }
    });

    const handleCurrencyChange = (event) => {
        formik.setFieldValue('amount', formatCurrency(event.target.value));
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
            {/* drawer-head */}
            <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, mb: '4px' }}>Editar despesa pendente</Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }} noWrap>
                        Edição rápida — ajuste valor, vencimento ou confirme o pagamento.
                    </Typography>
                </Box>
                <IconButton onClick={() => onClose && onClose()} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box component="form" onSubmit={formik.handleSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                {/* drawer-body */}
                <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                    {submitError ? (
                        <Alert severity="error" onClose={() => setSubmitError('')} sx={{ mb: 2 }}>
                            {submitError}
                        </Alert>
                    ) : null}

                    <TxCompactTypeBadge />

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            autoFocus
                            label="Descrição *"
                            name="description"
                            inputProps={{ maxLength: 120 }}
                            value={formik.values.description}
                            onChange={(e) => { setSubmitError(''); formik.handleChange(e); }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.description && Boolean(formik.errors.description)}
                            helperText={formik.touched.description && formik.errors.description}
                            variant="outlined"
                            sx={standaloneFieldSx}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            label="Valor (R$) *"
                            name="amount"
                            value={formik.values.amount}
                            onChange={(e) => { setSubmitError(''); handleCurrencyChange(e); }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.amount && Boolean(formik.errors.amount)}
                            helperText={formik.touched.amount && formik.errors.amount}
                            variant="outlined"
                            sx={standaloneFieldSx}
                        />
                    </Box>

                    <Box sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            label="Vencimento"
                            name="dueDate"
                            type="date"
                            value={formik.values.dueDate}
                            onChange={(e) => { setSubmitError(''); formik.handleChange(e); }}
                            onBlur={formik.handleBlur}
                            error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
                            helperText={
                                (formik.touched.dueDate && formik.errors.dueDate) ||
                                'Deixe em branco para manter o vencimento já cadastrado (não devolvido pela listagem).'
                            }
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                            sx={standaloneFieldSx}
                        />
                    </Box>

                    <Box sx={{ ...settingRowSx(true), pt: '6px' }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'background.subtle', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Wallet size={16} strokeWidth={2} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Pagar pelo caixa agora</Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                Ative para marcar esta despesa como paga agora — ela passa para &quot;Prestado
                                Contas&quot; imediatamente ao salvar.
                            </Typography>
                        </Box>
                        <Switch
                            checked={formik.values.payNow}
                            onChange={(e) => { setSubmitError(''); formik.setFieldValue('payNow', e.target.checked); }}
                        />
                    </Box>
                </Box>

                {/* drawer-foot */}
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                    <Button variant="outlined" onClick={() => onClose && onClose()} size="small" disabled={loading}>
                        Fechar
                    </Button>
                    <Button type="submit" variant="contained" size="small" disabled={loading}>
                        {loading ? 'Salvando…' : 'Salvar alterações'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
